import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { LogoutIcon, SunIcon, MoonIcon } from './icons';
import { useBranding } from '@/hooks/useBranding';
import { recordLogout } from '@/lib/logout-reason';

interface HeaderProps {
  title?: string;
  onOpenSidebar?: () => void;
}

export function Header({ title, onOpenSidebar }: HeaderProps) {
  const { name: brand } = useBranding();
  const { admin, clearAuth } = useAuthStore();
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggle);

  const handleLogout = () => {
    recordLogout({ kind: 'manual' });
    clearAuth();
  };

  return (
    <header className="h-14 sm:h-16 sticky top-0 z-20 bg-surface border-b border-border flex items-center justify-between px-3 sm:px-6">
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          onClick={onOpenSidebar}
          aria-label="Open menu"
          className="md:hidden p-2 -ml-2 text-content-muted hover:text-content hover:bg-surface-muted rounded-md"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Mobile branding (sidebar hidden) */}
        <span className="md:hidden text-base font-semibold text-content truncate">
          <span className="text-primary">{brand}</span>
          {title ? <span className="text-content-subtle"> · {title}</span> : null}
        </span>

        {title && (
          <h1 className="hidden md:block text-lg font-semibold text-content truncate">{title}</h1>
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-4 shrink-0">
        {admin && (
          <span className="hidden sm:inline text-sm text-content-muted truncate max-w-[200px]">
            {admin.email}
          </span>
        )}
        <button
          type="button"
          onClick={toggleTheme}
          aria-label="Toggle dark mode"
          className="p-2 text-content-subtle hover:text-content hover:bg-surface-muted rounded-md transition-colors"
        >
          {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
        </button>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-2 sm:px-3 py-1.5 text-sm font-medium text-content-muted hover:text-content hover:bg-surface-muted rounded-md transition-colors duration-150"
          aria-label="Logout"
        >
          <LogoutIcon className="w-4 h-4" />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
}
